import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { api } from "../lib/api";

const fld = "bg-input border-white/10 text-white rounded-sm";

const Section = ({ title, color, children }) => (
  <div className="bg-card border border-white/10 rounded-sm p-6">
    <h3 className="font-display font-bold text-lg tracking-tight mb-5" style={{ color }}>{title}</h3>
    {children}
  </div>
);

const Row = ({ label, children }) => (
  <div className="mb-4">
    <Label className="text-sm text-muted-foreground mb-2 block">{label}</Label>
    {children}
  </div>
);

function ListEditor({ items, setItems, fields, testPrefix }) {
  const update = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: key === "name" ? val : Number(val) };
    setItems(next);
  };
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          {fields.map((f) => (
            <Input
              key={f.key}
              data-testid={`${testPrefix}-${f.key}-${i}`}
              type={f.key === "name" ? "text" : "number"}
              step="0.01"
              value={it[f.key] ?? ""}
              placeholder={f.label}
              onChange={(e) => update(i, f.key, e.target.value)}
              className={`${fld} ${f.key === "name" ? "flex-1" : "w-28"}`}
            />
          ))}
          <Button data-testid={`${testPrefix}-del-${i}`} onClick={() => setItems(items.filter((_, x) => x !== i))} variant="ghost" size="icon" className="text-muted-foreground hover:text-neon-pink shrink-0">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button data-testid={`${testPrefix}-add`} onClick={() => setItems([...items, fields.reduce((a, f) => ({ ...a, [f.key]: f.key === "name" ? "" : 1 }), {})])}
        variant="outline" size="sm" className="border-white/20 text-white hover:border-neon-cyan hover:text-neon-cyan rounded-sm gap-1 mt-2">
        <Plus className="w-3 h-3" /> Aggiungi
      </Button>
    </div>
  );
}

export function SettingsEditor() {
  const [contacts, setContacts] = useState(null);
  const [quote, setQuote] = useState(null);
  const [texts, setTexts] = useState(null);
  const [notif, setNotif] = useState(null);
  const [promos, setPromos] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => { setContacts(r.data.contacts); setQuote(r.data.quote); setTexts(r.data.texts || {}); setNotif(r.data.notification_prefs || { events: {}, channels: {} }); setPromos(r.data.promos || []); });
    api.get("/settings/history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const setC = (k) => (e) => setContacts({ ...contacts, [k]: e.target.value });
  const setQ = (k) => (e) => setQuote({ ...quote, [k]: k === "currency" || k === "note" ? e.target.value : Number(e.target.value) });
  const setLaser = (k) => (e) => setQuote({ ...quote, laser: { ...quote.laser, [k]: k === "materials" ? e : Number(e.target.value) } });

  const saveContacts = async () => {
    setSaving(true);
    try { await api.put("/settings/contacts", contacts); toast.success("Contatti salvati"); }
    catch { toast.error("Errore nel salvataggio"); } finally { setSaving(false); }
  };
  const saveQuote = async () => {
    setSaving(true);
    try { await api.put("/settings/quote", quote); toast.success("Parametri preventivo salvati"); }
    catch { toast.error("Errore nel salvataggio"); } finally { setSaving(false); }
  };
  const setT = (k) => (e) => setTexts({ ...texts, [k]: e.target.value });
  const saveTexts = async () => {
    setSaving(true);
    try { await api.put("/settings/texts", texts); toast.success("Testi delle pagine salvati"); }
    catch { toast.error("Errore nel salvataggio"); } finally { setSaving(false); }
  };
  const setEvent = (k) => (v) => setNotif({ ...notif, events: { ...notif.events, [k]: v } });
  const setChannel = (k) => (v) => setNotif({ ...notif, channels: { ...notif.channels, [k]: v } });
  const saveNotif = async () => {
    setSaving(true);
    try { await api.put("/settings/notifications", notif); toast.success("Preferenze notifiche salvate"); }
    catch { toast.error("Errore nel salvataggio"); } finally { setSaving(false); }
  };

  const addPromo = () => setPromos([...promos, { id: `p${Date.now()}`, title: "", text: "", link_url: "", link_label: "", active: true, expires_at: "" }]);
  const updPromo = (i, k, v) => { const n = [...promos]; n[i] = { ...n[i], [k]: v }; setPromos(n); };
  const rmPromo = (i) => setPromos(promos.filter((_, k) => k !== i));
  const savePromos = async () => {
    setSaving(true);
    try { await api.put("/settings/promos", { promos }); toast.success("Promozioni salvate"); api.get("/settings/history").then((r) => setHistory(r.data)); }
    catch { toast.error("Errore nel salvataggio"); } finally { setSaving(false); }
  };
  const restoreVersion = async (sid) => {
    if (!window.confirm("Ripristinare questa versione delle impostazioni? Le modifiche attuali verranno comunque salvate nella cronologia.")) return;
    try { const r = await api.post(`/settings/restore/${sid}`); const d = r.data; setContacts(d.contacts); setQuote(d.quote); setTexts(d.texts || {}); setNotif(d.notification_prefs || { events: {}, channels: {} }); setPromos(d.promos || []); toast.success("Versione ripristinata"); api.get("/settings/history").then((x) => setHistory(x.data)); }
    catch { toast.error("Errore nel ripristino"); }
  };

  if (!contacts || !quote || !texts || !notif) return <p className="font-mono text-neon-cyan animate-pulse-glow mt-6">Caricamento…</p>;

  return (
    <div className="space-y-6 mt-6">
      <div className="grid lg:grid-cols-2 gap-6">
      {/* Contacts */}
      <Section title="Contatti" color="#00F0FF">
        <Row label="Email"><Input data-testid="set-email" value={contacts.email || ""} onChange={setC("email")} className={fld} /></Row>
        <Row label="WhatsApp (numero)"><Input data-testid="set-whatsapp" value={contacts.whatsapp || ""} onChange={setC("whatsapp")} placeholder="+39..." className={fld} /></Row>
        <Row label="Telefono"><Input data-testid="set-phone" value={contacts.phone || ""} onChange={setC("phone")} className={fld} /></Row>
        <Row label="Instagram (@utente o URL)"><Input data-testid="set-instagram" value={contacts.instagram || ""} onChange={setC("instagram")} className={fld} /></Row>
        <Row label="Facebook (pagina o URL)"><Input data-testid="set-facebook" value={contacts.facebook || ""} onChange={setC("facebook")} className={fld} /></Row>
        <Row label="Dove sono"><Input data-testid="set-location" value={contacts.location || ""} onChange={setC("location")} className={fld} /></Row>
        <Row label="Testo introduttivo"><Input data-testid="set-intro" value={contacts.intro || ""} onChange={setC("intro")} className={fld} /></Row>
        <Button data-testid="save-contacts" onClick={saveContacts} disabled={saving} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2 mt-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva contatti
        </Button>
      </Section>

      {/* Quote */}
      <Section title="Preventivo — Stampa 3D" color="#39FF14">
        <div className="grid grid-cols-3 gap-3">
          <Row label="Valuta"><Input data-testid="set-currency" value={quote.currency || ""} onChange={setQ("currency")} className={fld} /></Row>
          <Row label="Setup fee"><Input data-testid="set-setup" type="number" step="0.01" value={quote.setup_fee ?? 0} onChange={setQ("setup_fee")} className={fld} /></Row>
          <Row label="Prezzo min"><Input data-testid="set-min" type="number" step="0.01" value={quote.min_price ?? 0} onChange={setQ("min_price")} className={fld} /></Row>
        </div>
        <Label className="text-sm text-muted-foreground mb-2 block mt-2">Materiali (nome · €/grammo)</Label>
        <ListEditor items={quote.materials || []} setItems={(v) => setQuote({ ...quote, materials: v })} testPrefix="mat" fields={[{ key: "name", label: "Nome" }, { key: "price_per_gram", label: "€/g" }]} />
        <Label className="text-sm text-muted-foreground mb-2 block mt-4">Qualità (nome · moltiplicatore)</Label>
        <ListEditor items={quote.qualities || []} setItems={(v) => setQuote({ ...quote, qualities: v })} testPrefix="qual" fields={[{ key: "name", label: "Nome" }, { key: "multiplier", label: "x" }]} />
        <Label className="text-sm text-muted-foreground mb-2 block mt-4">Urgenza (nome · moltiplicatore)</Label>
        <ListEditor items={quote.urgency || []} setItems={(v) => setQuote({ ...quote, urgency: v })} testPrefix="urg" fields={[{ key: "name", label: "Nome" }, { key: "multiplier", label: "x" }]} />

        <h4 className="font-display font-bold text-base tracking-tight mt-6 mb-3 text-neon-pink">Incisione Laser</h4>
        <div className="grid grid-cols-3 gap-3">
          <Row label="€/minuto"><Input data-testid="set-laser-min" type="number" step="0.01" value={quote.laser?.price_per_minute ?? 0} onChange={setLaser("price_per_minute")} className={fld} /></Row>
          <Row label="Setup fee"><Input data-testid="set-laser-setup" type="number" step="0.01" value={quote.laser?.setup_fee ?? 0} onChange={setLaser("setup_fee")} className={fld} /></Row>
          <Row label="Prezzo min"><Input data-testid="set-laser-minprice" type="number" step="0.01" value={quote.laser?.min_price ?? 0} onChange={setLaser("min_price")} className={fld} /></Row>
        </div>
        <Label className="text-sm text-muted-foreground mb-2 block mt-2">Materiali laser (nome · moltiplicatore)</Label>
        <ListEditor items={quote.laser?.materials || []} setItems={(v) => setQuote({ ...quote, laser: { ...quote.laser, materials: v } })} testPrefix="lmat" fields={[{ key: "name", label: "Nome" }, { key: "multiplier", label: "x" }]} />

        <Row label="Nota mostrata al cliente"><Input data-testid="set-note" value={quote.note || ""} onChange={setQ("note")} className={`${fld} mt-4`} /></Row>
        <Button data-testid="save-quote" onClick={saveQuote} disabled={saving} className="bg-neon-green text-black hover:bg-white rounded-sm font-semibold gap-2 mt-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva preventivo
        </Button>
      </Section>
      </div>

      <Section title="Testi delle pagine & Informativa Privacy" color="#A855F7">
        <div className="grid md:grid-cols-2 gap-4">
          <Row label="Titolo Home (hero)"><Input data-testid="set-hero-title" value={texts.hero_title || ""} onChange={setT("hero_title")} className={fld} /></Row>
          <Row label="Intro Galleria"><Input data-testid="set-gallery-intro" value={texts.gallery_intro || ""} onChange={setT("gallery_intro")} className={fld} /></Row>
        </div>
        <Row label="Sottotitolo Home (hero)"><Textarea data-testid="set-hero-sub" rows={2} value={texts.hero_subtitle || ""} onChange={setT("hero_subtitle")} className={`${fld} resize-none`} /></Row>
        <Row label="Testo informativa privacy (GDPR) mostrato ai clienti"><Textarea data-testid="set-privacy" rows={6} value={texts.privacy_text || ""} onChange={setT("privacy_text")} className={`${fld} resize-none`} /></Row>
        <Button data-testid="save-texts" onClick={saveTexts} disabled={saving} className="text-white hover:opacity-90 rounded-sm font-semibold gap-2 mt-2" style={{ backgroundColor: "#A855F7" }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva testi
        </Button>
      </Section>

      <Section title="Notifiche amministratore" color="#00F0FF">
        <p className="text-sm text-muted-foreground font-light mb-5">Scegli su quali eventi vuoi essere avvisato e con quale canale.</p>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-neon-cyan mb-3 block">Eventi</Label>
            <div className="space-y-3">
              {[
                { k: "new_request", label: "Nuova richiesta ricevuta" },
                { k: "new_review", label: "Nuova recensione ricevuta" },
                { k: "loyalty_milestone", label: "Cliente raggiunge il 10° ordine" },
                { k: "order_tracked", label: "Un cliente consulta il proprio ordine" },
              ].map((e) => (
                <div key={e.k} className="flex items-center justify-between gap-3 py-1">
                  <span className="text-sm text-foreground font-light">{e.label}</span>
                  <Switch data-testid={`notif-event-${e.k}`} aria-label={e.label} checked={notif.events?.[e.k] ?? true} onCheckedChange={setEvent(e.k)} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-neon-green mb-3 block">Canali</Label>
            <div className="space-y-3">
              {[
                { k: "email", label: "Email (Resend)" },
                { k: "in_app", label: "Campanella nella Dashboard" },
                { k: "push", label: "Notifiche push del telefono" },
              ].map((c) => (
                <div key={c.k} className="flex items-center justify-between gap-3 py-1">
                  <span className="text-sm text-foreground font-light">{c.label}</span>
                  <Switch data-testid={`notif-channel-${c.k}`} aria-label={c.label} checked={notif.channels?.[c.k] ?? true} onCheckedChange={setChannel(c.k)} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <Button data-testid="save-notif" onClick={saveNotif} disabled={saving} className="bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold gap-2 mt-6">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva notifiche
        </Button>
      </Section>

      <Section title="Promozioni Home" color="#FF007F">
        <p className="text-sm text-muted-foreground font-light mb-4">Banner temporanei mostrati in home. Attiva/disattiva e imposta una scadenza opzionale (si nasconde da sola).</p>
        <div className="space-y-4">
          {promos.map((p, i) => (
            <div key={p.id} data-testid={`promo-item-${i}`} className="border border-white/10 rounded-sm p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Input data-testid={`promo-title-${i}`} value={p.title} onChange={(e) => updPromo(i, "title", e.target.value)} placeholder="Titolo promozione" className={fld} />
                <div className="flex items-center gap-2 shrink-0">
                  <Switch data-testid={`promo-active-${i}`} checked={p.active !== false} onCheckedChange={(v) => updPromo(i, "active", v)} aria-label="Attiva promozione" />
                  <button data-testid={`promo-remove-${i}`} onClick={() => rmPromo(i)} className="text-muted-foreground hover:text-neon-pink transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <Textarea data-testid={`promo-text-${i}`} value={p.text} onChange={(e) => updPromo(i, "text", e.target.value)} placeholder="Testo della promozione" className={fld} />
              <div className="grid sm:grid-cols-3 gap-2">
                <Input data-testid={`promo-link-${i}`} value={p.link_url || ""} onChange={(e) => updPromo(i, "link_url", e.target.value)} placeholder="Link (facoltativo)" className={fld} />
                <Input data-testid={`promo-linklabel-${i}`} value={p.link_label || ""} onChange={(e) => updPromo(i, "link_label", e.target.value)} placeholder="Testo bottone" className={fld} />
                <Input data-testid={`promo-expires-${i}`} type="date" value={(p.expires_at || "").slice(0, 10)} onChange={(e) => updPromo(i, "expires_at", e.target.value)} className={fld} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button data-testid="promo-add" onClick={addPromo} variant="outline" className="rounded-sm gap-2 border-white/15"><Plus className="w-4 h-4" /> Aggiungi promozione</Button>
          <Button data-testid="promo-save" onClick={savePromos} disabled={saving} className="bg-neon-pink text-white hover:opacity-90 rounded-sm gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva promozioni</Button>
        </div>
      </Section>

      <Section title="Ripristina modifiche" color="#A855F7">
        <p className="text-sm text-muted-foreground font-light mb-4">Ultime modifiche alle impostazioni (home, contatti, preventivo, promozioni). Puoi tornare a una versione precedente.</p>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground font-mono">Nessuna versione salvata.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={h.id} data-testid={`history-item-${i}`} className="flex items-center justify-between gap-3 border border-white/10 rounded-sm px-4 py-2">
                <span className="text-sm font-mono text-muted-foreground">{new Date(h.at).toLocaleString("it-IT")}</span>
                <Button data-testid={`history-restore-${i}`} onClick={() => restoreVersion(h.id)} variant="outline" size="sm" className="rounded-sm border-white/15">Ripristina</Button>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
